<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <AppFormWrapper id="email-verify-form" @submit.prevent="onSubmit">
    <h2>Verify</h2>
    <p>
      Enter the 6-digit code sent to <strong>{{ newEmail }}</strong
      >.
    </p>
    <CodeBox />
    <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
  </AppFormWrapper>
</template>

<script setup>
import {computed, provide, ref, watch, watchEffect} from "vue";
import AppFormWrapper from "@/components/App/Form/Wrapper";
import CodeBox from "@/components/App/Modal/SignIn/CodeBox";
import {useEmailChange} from "@/composables/useEmailChange";
import {postEmailAction} from "@/composables/useEmailEdit";

// isMerge: the code is ours (merge-start), checked by merge-confirm, which merges the accounts;
// otherwise Clerk's, for the address being added
const props = defineProps({
  newEmail: {type: String, default: ""},
  isMerge: {type: Boolean, default: false},
});
const emit = defineEmits(["success", "update:canSubmit"]);

const {pendingEmailAddress} = useEmailChange();
const verificationCode = ref("");
const loading = ref(false);
const errorMessage = ref("");

const isReady = computed(() => verificationCode.value.length === 6);
const canSubmit = computed(() => isReady.value && !loading.value);
watch(canSubmit, (val) => emit("update:canSubmit", val), {immediate: true});

provide("verificationCode", verificationCode);

async function checkCode(code) {
  if (!props.isMerge) return await pendingEmailAddress.value.attemptVerification({code});
  const result = await postEmailAction("merge-confirm", {code});
  if (!result.ok) throw new Error(result.message);
}

async function onSubmit() {
  if (!isReady.value || loading.value) return;
  if (!props.isMerge && !pendingEmailAddress.value) return;
  errorMessage.value = "";
  loading.value = true;
  try {
    await checkCode(verificationCode.value);
    verificationCode.value = "";
    emit("success");
  } catch (error) {
    errorMessage.value = error?.errors?.[0]?.message || error?.message
      || "Incorrect code. Please try again.";
    verificationCode.value = "";
  } finally {
    loading.value = false;
  }
}

watchEffect(() => {
  if (isReady.value && !loading.value) onSubmit();
});
</script>
